import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { COMMON_REQUEST_DATETIME_FORMAT } from 'constants/date';

import { closeDialogAction } from 'store/global/actions';

import {
  bulkPasteButtonsStyles,
  bulkPasteTextAreaStyles,
  bulkPasteWrapperStyles,
} from './InvestorConditions.styles';
import { InvestorConditionsTestIds } from './InvestorConditions.testIds';

const BulkPasteModal = ({ dialogId, onAddConditions }) => {
  const dispatch = useDispatch();

  const [text, setText] = useState('');

  const handleTextChange = useCallback(e => setText(e.target.value), []);

  const handleClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const handlePaste = useCallback(() => {
    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length);

    const newConditions = lines.map(line => ({
      id: crypto.randomUUID(),
      conditionText: line,
      dateAdded: dayjs().format(COMMON_REQUEST_DATETIME_FORMAT),
      dateResponded: null,
      dateCleared: null,
    }));

    onAddConditions(newConditions);
    handleClose();
  }, [text, onAddConditions, handleClose]);

  return (
    <Box sx={bulkPasteWrapperStyles}>
      <Typography variant="regular14">
        Paste conditions below, one per line. Each line will be added as a separate condition with
        today&apos;s date.
      </Typography>
      <TextFieldCDS
        multiline
        ariaLabel="Paste conditions here, one per line..."
        dataTestId={InvestorConditionsTestIds.BULK_PASTE_INPUT}
        minRows={8}
        name="bulkConditionsText"
        placeholder="Paste conditions here, one per line..."
        sx={bulkPasteTextAreaStyles}
        value={text}
        onChange={handleTextChange}
      />
      <ButtonGroupWrapper sx={bulkPasteButtonsStyles}>
        <Button
          data-testid={InvestorConditionsTestIds.BULK_PASTE_CANCEL_BUTTON}
          size="large"
          variant="line"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={InvestorConditionsTestIds.BULK_PASTE_ADD_BUTTON}
          disabled={!text.trim().length}
          size="large"
          onClick={handlePaste}
        >
          Add Conditions
        </Button>
      </ButtonGroupWrapper>
    </Box>
  );
};

BulkPasteModal.propTypes = {
  dialogId: PropTypes.string.isRequired,
  onAddConditions: PropTypes.func.isRequired,
};

export default memo(BulkPasteModal);
